import React, { useEffect } from "react";
import "./styles.scss";
import CircularProgress from "@mui/material/CircularProgress";
import { connect } from "react-redux";
import DataRow from "./DataRow";
import {
  getDate,
  toFixedVal,
  getStoreChecker,
  roundValue,
} from "../../utils/utils";
import { useLazyQuery } from "@apollo/client";
import {
  GET_INVENTORY_CONTROL_DRIVER_TAB_DETAIL,
  GET_DEFAULT_LOCATION,
} from "../../graphql/queries.js";
import ErrorMessage from "../ErrorMessage";
import {
  DATA_FETCH_ERROR,
  ONLOAN_INVALID_STOREID_ERROR,
  DRIVER_TOTAL_LABEL,
  DRIVER_REPLACE_LABEL,
  DRIVER_DAMAGE_LABEL,
  DRIVER_MOS_LABEL,
  DRIVER_KNOWN_THEFT_LABEL,
  DRIVER_SHIPMENT_LABEL,
  DRIVER_CANDY_LABEL,
} from "../../common/constants";
import PropTypes from "prop-types";

const InventoryControlDriversTab = ({ employeeInfo = {}, dateMatrix }) => {
  const [selectedLocation, setSelectedLocation] = React.useState(
    JSON.parse(sessionStorage.getItem("selectedLocation"))
  );
  let selectedStoreId = localStorage.getItem("storeId");

  const activeStorechecker = getStoreChecker(selectedStoreId);
  const [getDefaultLocation, { error: locationError = {} }] = useLazyQuery(
    GET_DEFAULT_LOCATION,
    {
      context: {
        headers: {
          Authorization: localStorage.getItem("jwtToken"),
        },
      },
      onCompleted: (data) => {
        let { salesSummaryV2 } = data;
        if (selectedLocation.locationName === "NA") {
          setSelectedLocation({
            locationNo: salesSummaryV2?.location?.primaryLocationNumber,
            locationName: salesSummaryV2?.location?.primaryLocationName,
          });
          sessionStorage.setItem(
            "selectedLocation",
            JSON.stringify({
              locationNo: salesSummaryV2?.location?.primaryLocationNumber,
              locationName: salesSummaryV2?.location?.primaryLocationName,
            })
          );
        }
      },
    }
  );

  const userEmail = employeeInfo.adUpn;

  useEffect(() => {
    if (userEmail && (activeStorechecker === true || selectedStoreId === "")) {
      getDefaultLocation({
        variables: { request: { email: userEmail, storeId: selectedStoreId } },
      });
    }
  }, [userEmail, getDefaultLocation, selectedStoreId, activeStorechecker]);

  const [
    getInventoryControlDriversTabDetails,
    {
      data: { inventoryControl: { invControlDrivers } = {} } = {},
      loading,
      error = {},
    },
  ] = useLazyQuery(GET_INVENTORY_CONTROL_DRIVER_TAB_DETAIL, {
    context: {
      headers: {
        Authorization: localStorage.getItem("jwtToken"),
      },
    },
  });

  useEffect(() => {
    if (
      userEmail &&
      dateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== "NA"
    ) {
      getInventoryControlDriversTabDetails({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate("YESTERDAY", ""),
            matrixType: dateMatrix,
          },
        },
      });
    }
  }, [
    userEmail,
    dateMatrix,
    getInventoryControlDriversTabDetails,
    selectedLocation,
  ]);

  const {
    replace = {
      unitQuantity: 0,
      itemCount: 0,
      adjustmentsAmount: 0,
    },
    damages = {
      unitQuantity: 0,
      itemCount: 0,
      adjustmentsAmount: 0,
    },
    markedOutOfStock = {
      unitQuantity: 0,
      itemCount: 0,
      adjustmentsAmount: 0,
    },
    total = {
      unitQuantity: 0,
      itemCount: 0,
      adjustmentsAmount: 0,
    },
    knownTheft = {
      unitQuantity: 0,
      itemCount: 0,
      adjustmentsAmount: 0,
    },
    shipment = {
      unitQuantity: 0,
      itemCount: 0,
      adjustmentsAmount: 0,
    },
    candy = {
      unitQuantity: 0,
      itemCount: 0,
      adjustmentsAmount: 0,
    },
  } = invControlDrivers || {};

  return (
    <div className="inventory-detail-tab">
      {loading && (
        <div className="inventory-detail-loader">
          <CircularProgress color="primary" size={30} />
        </div>
      )}
      {!loading && (
        <>
          {error.message || locationError.message ? (
            <ErrorMessage
              statusCode={
                error?.networkError?.statusCode ||
                locationError?.networkError?.statusCode
              }
              message={DATA_FETCH_ERROR}
            />
          ) : null}
          {activeStorechecker === false && selectedStoreId ? (
            <ErrorMessage message={ONLOAN_INVALID_STOREID_ERROR} />
          ) : null}
          <div className="inventory-detail-table-container">
            <div className="table-content">
              <div className="data-row-container">
                <DataRow
                  title={DRIVER_TOTAL_LABEL}
                  unitQuantity={roundValue(total?.unitQuantity)}
                  itemCount={roundValue(total?.itemCount)}
                  adjustmentsAmount={toFixedVal(total?.adjustmentsAmount, "$", "start", 0)}
                />
              </div>
              <div className="data-row-container">
                <DataRow
                  title={DRIVER_REPLACE_LABEL}
                  unitQuantity={roundValue(replace?.unitQuantity)}
                  itemCount={roundValue(replace?.itemCount)}
                  adjustmentsAmount={toFixedVal(replace?.adjustmentsAmount, "$", "start", 0)}
                />
              </div>
              <div className="data-row-container">
                <DataRow
                  title={DRIVER_DAMAGE_LABEL}
                  itemCount={roundValue(damages?.itemCount)}
                  unitQuantity={roundValue(damages?.unitQuantity)}
                  adjustmentsAmount={toFixedVal(damages?.adjustmentsAmount, "$", "start", 0)}
                />
              </div>
              <div className="data-row-container">
                <DataRow
                  title={DRIVER_MOS_LABEL}
                  itemCount={roundValue(markedOutOfStock?.itemCount)}
                  unitQuantity={roundValue(markedOutOfStock?.unitQuantity)}
                  adjustmentsAmount={toFixedVal(
                    markedOutOfStock?.adjustmentsAmount,
                    "$",
                    "start",
                    0
                  )}
                />
              </div>
              <div className="data-row-container">
                <DataRow
                  title={DRIVER_KNOWN_THEFT_LABEL}
                  itemCount={roundValue(knownTheft?.itemCount)}
                  unitQuantity={roundValue(knownTheft?.unitQuantity)}
                  adjustmentsAmount={toFixedVal(knownTheft?.adjustmentsAmount, "$", "start", 0)}
                />
              </div>
              <div className="data-row-container">
                <DataRow
                  title={DRIVER_SHIPMENT_LABEL}
                  itemCount={roundValue(shipment?.itemCount)}
                  unitQuantity={roundValue(shipment?.unitQuantity)}
                  adjustmentsAmount={toFixedVal(shipment?.adjustmentsAmount, "$", "start", 0)}
                />
              </div>
              <div className="data-row-container">
                <DataRow
                  title={DRIVER_CANDY_LABEL}
                  itemCount={roundValue(candy?.itemCount)}
                  unitQuantity={roundValue(candy?.unitQuantity)}
                  adjustmentsAmount={toFixedVal(candy?.adjustmentsAmount, "$", "start", 0)}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

InventoryControlDriversTab.propTypes = {
  employeeInfo: PropTypes.shape({ adUpn: PropTypes.string }),
  dateMatrix: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    employeeInfo: state.auth.employeeInfo,
    dateMatrix: state.salesSummary.currentDateMatrix.value,
  };
};
export default connect(mapStateToProps, undefined)(InventoryControlDriversTab);
